import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import {
  Severity,
  valuesExist,
  type ValidationRuleGenerator
} from "../scenarioValidator";

export const obsolescenceChecksNames = [
  "shroud_obsolete",
  "top_casing_obsolete",
  "bottom_casing_obsolete",
  "top_tubing_obsolete",
  "bottom_tubing_obsolete",
  "intake_obsolete",
  "pump_obsolete",
  "housing_obsolete",
  "motor_obsolete",
  "seal_obsolete",
  "sensor_obsolete",
  "mle_obsolete",
  "cable_obsolete",
  "up_trans_obsolete",
  "down_trans_obsolete",
  "switchboard_obsolete",
  "vsd_obsolete"
] as const;

export const obsolescenceChecks: ValidationRuleGenerator = ({
  wellProperties,
  calcs,
  equipment
}) => [
  {
    name: "top_casing_obsolete",
    message: "Top casing is marked obsolete. Please choose another",
    severity: Severity.error,
    isActive: valuesExist([wellProperties.value?.top_casing_id]),
    isInvalid: Boolean(wellProperties.value?.r_top_casing?.deleted_at),
    rulePlacement: EquipmentCalculatorSectionKey.WELL_COMPLETION
  },
  {
    name: "bottom_casing_obsolete",
    message: "Bottom casing is marked obsolete. Please choose another",
    severity: Severity.error,
    isActive: valuesExist([wellProperties.value?.bottom_casing_id]),
    isInvalid: Boolean(wellProperties.value?.r_bottom_casing?.deleted_at),
    rulePlacement: EquipmentCalculatorSectionKey.WELL_COMPLETION
  },
  {
    name: "top_tubing_obsolete",
    message: "Top tubing is marked obsolete. Please choose another",
    severity: Severity.error,
    isActive: valuesExist([wellProperties.value?.top_tubing_id]),
    isInvalid: Boolean(wellProperties.value?.r_top_tubing?.deleted_at),
    rulePlacement: EquipmentCalculatorSectionKey.OPERATING_PARAMETERS
  },
  {
    name: "bottom_tubing_obsolete",
    message: "Bottom tubing is marked obsolete. Please choose another",
    severity: Severity.error,
    isActive: valuesExist([wellProperties.value?.bottom_tubing_id]),
    isInvalid: Boolean(wellProperties.value?.r_bottom_tubing?.deleted_at),
    rulePlacement: EquipmentCalculatorSectionKey.OPERATING_PARAMETERS
  },
  {
    name: "intake_obsolete",
    message: "Intake is marked obsolete. Please choose another",
    severity: Severity.error,
    isActive: calcs.orderedIntakes.value.length > 0,
    isInvalid: calcs.orderedIntakes.value.some(
      xref => xref.r_intake?.deleted_at
    ),
    affectedXrefIds: calcs.orderedIntakes.value
      .filter(xref => xref.r_intake?.deleted_at)
      .map(xref => xref.id),
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  },
  {
    name: "pump_obsolete",
    message: "Pump is marked obsolete. Please choose another",
    severity: Severity.error,
    isActive: calcs.ascendingOrderPumps.value.length > 0,
    isInvalid: calcs.ascendingOrderPumps.value.some(
      xref => xref.r_pump?.deleted_at
    ),
    affectedXrefIds: calcs.ascendingOrderPumps.value
      .filter(xref => xref.r_pump?.deleted_at)
      .map(xref => xref.id),
    rulePlacement: EquipmentCalculatorSectionKey.PUMP
  },
  {
    name: "housing_obsolete",
    message: "Housing is marked obsolete. Please choose another",
    severity: Severity.error,
    isActive:
      calcs.ascendingOrderPumps.value.flatMap(pump => pump.r_xref_housings)
        .length > 0,
    isInvalid: calcs.ascendingOrderPumps.value
      .flatMap(pumpXref => pumpXref.r_xref_housings)
      .some(housingXref => housingXref.r_housing?.deleted_at),
    affectedXrefIds: calcs.ascendingOrderPumps.value
      .flatMap(pumpXref => pumpXref.r_xref_housings)
      .filter(housingXref => housingXref.r_housing?.deleted_at)
      .map(housingXref => housingXref.id),
    rulePlacement: EquipmentCalculatorSectionKey.PUMP
  },
  {
    name: "motor_obsolete",
    message: "Motor is marked obsolete. Please choose another",
    severity: Severity.error,
    isActive: valuesExist([equipment.value?.motor_id]),
    isInvalid: Boolean(equipment.value?.r_motor?.deleted_at),
    rulePlacement: EquipmentCalculatorSectionKey.MOTOR
  },
  {
    name: "seal_obsolete",
    message: "Seal is marked obsolete. Please choose another",
    severity: Severity.error,
    isActive: calcs.descendingOrderSeals.value.length > 0,
    isInvalid: calcs.descendingOrderSeals.value.some(
      xref => xref.r_seal?.deleted_at
    ),
    affectedXrefIds: calcs.descendingOrderSeals.value
      .filter(xref => xref.r_seal?.deleted_at)
      .map(xref => xref.id),
    rulePlacement: EquipmentCalculatorSectionKey.SEAL
  },
  {
    name: "sensor_obsolete",
    message: "Sensor is marked obsolete. Please choose another",
    severity: Severity.error,
    isActive: valuesExist([equipment.value?.sensor_id]),
    isInvalid: Boolean(equipment.value?.r_sensor?.deleted_at),
    rulePlacement: EquipmentCalculatorSectionKey.MLE
  },
  {
    name: "mle_obsolete",
    message: "MLE is marked obsolete. Please choose another",
    severity: Severity.error,
    isActive: valuesExist([equipment.value?.mle_id]),
    isInvalid: Boolean(equipment.value?.r_mle?.deleted_at),
    rulePlacement: EquipmentCalculatorSectionKey.MLE
  },
  {
    name: "cable_obsolete",
    message: "Cable is marked obsolete. Please choose another",
    severity: Severity.error,
    isActive: calcs.orderedCables.value.length > 0,
    isInvalid: calcs.orderedCables.value.some(xref => xref.r_cable?.deleted_at),
    affectedXrefIds: calcs.orderedCables.value
      .filter(xref => xref.r_cable?.deleted_at)
      .map(xref => xref.id),
    rulePlacement: EquipmentCalculatorSectionKey.CABLE
  },
  {
    name: "up_trans_obsolete",
    message: "Step up transformer is marked obsolete. Please choose another",
    severity: Severity.error,
    isActive: valuesExist([equipment.value?.up_trans_id]),
    isInvalid: Boolean(equipment.value?.r_step_up_transformer?.deleted_at),
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE
  },
  {
    name: "down_trans_obsolete",
    message: "Step down transformer is marked obsolete. Please choose another",
    severity: Severity.error,
    isActive: valuesExist([equipment.value?.down_trans_id]),
    isInvalid: Boolean(equipment.value?.r_step_down_transformer?.deleted_at),
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE
  },
  {
    name: "switchboard_obsolete",
    message: "Switchboard is marked obsolete. Please choose another",
    severity: Severity.error,
    isActive: valuesExist([equipment.value?.switchboard_id]),
    isInvalid: Boolean(equipment.value?.r_switchboard?.deleted_at),
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE
  },
  {
    name: "vsd_obsolete",
    message: "VSD is marked obsolete. Please choose another",
    severity: Severity.error,
    isActive: valuesExist([equipment.value?.vsd_id]),
    isInvalid: Boolean(equipment.value?.r_vsd?.deleted_at),
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE
  }
];
